import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import { MentionsInput, Mention, SuggestionDataItem } from 'react-mentions';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import './mention-textarea.scss';

export interface MentionedUser {
  userId: string;
  displayName: string;
  currentPermission?: 'NONE' | 'READ' | 'WRITE' | 'ADMIN' | 'OWNER';
}

interface CompanyUser {
  id: string;
  name: string;
  currentPermission?: 'NONE' | 'READ' | 'WRITE' | 'ADMIN' | 'OWNER';
}

interface MentionTextareaProps {
  value: string;
  onChange: (value: string, mentions: MentionedUser[]) => void;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  placeholder?: string;
  disabled?: boolean;
  threadId?: string | null;
}

const MentionTextarea: FC<MentionTextareaProps> = ({
  value,
  onChange,
  onKeyDown,
  placeholder,
  disabled = false,
  threadId,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [users, setUsers] = useState<CompanyUser[]>([]);
  const [loading, setLoading] = useState(false);
  const mentionsRef = useRef<MentionedUser[]>([]);

  // Fetch company users
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const token = await getAccessTokenSilently();
        const response = await api.get('/agents/users/company', {
          params: threadId ? { thread_id: threadId } : {},
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success) {
          setUsers(response.data.users);
        }
      } catch (error) {
        console.error('Error fetching company users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [getAccessTokenSilently, threadId]);

  // Convert users to suggestion format
  const suggestions: SuggestionDataItem[] = users.map((user) => ({
    id: user.id,
    display: user.name,
  }));

  // Handle value change and extract mentions
  const handleChange = useCallback(
    (
      event: { target: { value: string } },
      newValue: string,
      newPlainTextValue: string,
      mentions: { id: string; display: string }[],
    ) => {
      // Map mentions to our format with permission info
      const mentionedUsers: MentionedUser[] = mentions.map((mention) => {
        const user = users.find((u) => u.id === mention.id);
        return {
          userId: mention.id,
          displayName: mention.display,
          currentPermission: user?.currentPermission,
        };
      });

      mentionsRef.current = mentionedUsers;
      onChange(newValue, mentionedUsers);
    },
    [onChange, users],
  );

  // Handle key down - need to extract plain text for Enter handling
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
      if (onKeyDown) {
        onKeyDown(e);
      }
    },
    [onKeyDown],
  );

  return (
    <MentionsInput
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      disabled={disabled}
      className='mention-textarea'
      allowSpaceInQuery
    >
      <Mention
        trigger='@'
        data={suggestions}
        className='mention'
        displayTransform={(id, display) => `@${display}`}
        markup='@[__display__](__id__)'
        appendSpaceOnAdd
      />
    </MentionsInput>
  );
};

export default MentionTextarea;
